/**
 * Utilities for wrapping AI-formatted responses with greetings, closings, and signatures.
 */

const GREETINGS = [
  // Original 5
  (name: string) =>
    `Hi Dr. ${name}, one of our specialists has reviewed your inquiry and provided the following response`,
  (name: string) => `Dr. ${name}, we've received a response from our expert regarding your case`,
  (name: string) =>
    `Hello Dr. ${name}, our expert has had a chance to review your inquiry and shared the following`,
  (name: string) => `Dr. ${name}, here's what our specialist had to say about your inquiry`,
  (name: string) =>
    `Hi Dr. ${name}, our expert has reviewed your case and put together the following response`,
  // 5 new greetings
  (name: string) =>
    `Dr. ${name}, thank you for reaching out. Our specialist has provided the following insights`,
  (name: string) =>
    `Hi Dr. ${name}, we appreciate your inquiry. Here's the expert feedback on your case`,
  (name: string) =>
    `Hello Dr. ${name}, our team has carefully reviewed your question and prepared this response`,
  (name: string) =>
    `Dr. ${name}, our expert has thoroughly considered your inquiry and offers the following guidance`,
  (name: string) => `Hi Dr. ${name}, we wanted to share our specialist's perspective on your case`,
];

const CLOSINGS = [
  // Original 5
  () => `We're here if you have any follow-up questions!`,
  (name: string) =>
    `Please let us know if you'd like any clarification or have additional questions, Dr. ${name}`,
  () => `Feel free to reach out if anything else comes up!`,
  (name: string) => `Don't hesitate to follow up if you need anything else, Dr. ${name}!`,
  () => `We're happy to help with any additional questions you might have!`,
  // 5 new closings
  () => `Let us know if there's anything else we can help clarify!`,
  (name: string) => `We hope this helps, Dr. ${name}. Reach out anytime with follow-up questions!`,
  () => `As always, we're here to support you with any additional inquiries!`,
  (name: string) =>
    `Thank you for trusting us with your question, Dr. ${name}. We're just a message away!`,
  () => `Please don't hesitate to reach out if you need further assistance!`,
];

const SIGNATURE = 'Kate (Primum Support)';

function getRandomItem<T>(items: T[]): T {
  return items[Math.floor(Math.random() * items.length)];
}

function getRandomGreeting(recipientName: string): string {
  const greetingFn = getRandomItem(GREETINGS);

  return greetingFn(recipientName);
}

function getRandomClosing(recipientName: string): string {
  const closingFn = getRandomItem(CLOSINGS);

  return closingFn(recipientName);
}

type WrapFormattedResponseOptions = {
  aiFormattedResponse: string;
  recipientLastName: string;
  expertLastName: string;
};

/**
 * Wraps an AI-formatted response with a greeting, expert attribution, closing, and signature.
 */
export function wrapFormattedResponse(options: WrapFormattedResponseOptions): string {
  const { aiFormattedResponse, recipientLastName, expertLastName } = options;

  const greeting = getRandomGreeting(recipientLastName);

  const closing = getRandomClosing(recipientLastName);

  const expertAttribution = expertLastName ? `Dr. ${expertLastName}` : '';

  const sections = [
    greeting,
    '---',
    aiFormattedResponse,
    ...(expertAttribution ? [expertAttribution] : []),
    '---',
    closing,
    SIGNATURE,
  ];

  return sections.join('\n\n');
}
